import { memo } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { JobLocationsFromJobs } from '@/components/JobLocationsFromJobs'
import { ROLE_OPTIONS, PROJECT_TYPE_OPTIONS, DATE_FILTER_OPTIONS } from '@/constants/jobOptions'

interface OptimizedJobFiltersProps {
  selectedRole: string
  onRoleChange: (value: string) => void
  selectedLocation: string
  onLocationChange: (value: string) => void
  selectedProjectType: string
  onProjectTypeChange: (value: string) => void
  selectedDateFilter: string
  onDateFilterChange: (value: string) => void
}

// Memoized filter components to prevent unnecessary re-renders
const MemoizedRoleSelect = memo(({ selectedRole, onRoleChange }: { selectedRole: string; onRoleChange: (value: string) => void }) => (
  <Select value={selectedRole} onValueChange={onRoleChange}>
    <SelectTrigger aria-label={`Filter by role, currently selected: ${selectedRole === 'all' ? 'All Roles' : selectedRole}`}>
      <SelectValue placeholder="Select role" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">All Roles</SelectItem>
      {ROLE_OPTIONS.map((role) => (
        <SelectItem 
          key={role.value} 
          value={role.value}
          disabled={'isCategory' in role && role.isCategory}
          className={
            'isCategory' in role && role.isCategory 
              ? 'font-bold text-foreground text-base py-3 cursor-default' 
              : 'ml-4 text-sm'
          }
        >
          {('isCategory' in role && role.isCategory) ? role.label.replace(/\*\*/g, '') : role.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
))

const MemoizedProjectTypeSelect = memo(({ selectedProjectType, onProjectTypeChange }: { selectedProjectType: string; onProjectTypeChange: (value: string) => void }) => (
  <Select value={selectedProjectType} onValueChange={onProjectTypeChange}>
    <SelectTrigger aria-label={`Filter by project type, currently selected: ${selectedProjectType === 'all' ? 'All Projects' : selectedProjectType}`}>
      <SelectValue placeholder="Select project type" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">All Projects</SelectItem>
      {PROJECT_TYPE_OPTIONS.map((type) => (
        <SelectItem key={type.value} value={type.value}>
          {type.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
))

const MemoizedDateFilterSelect = memo(({ selectedDateFilter, onDateFilterChange }: { selectedDateFilter: string; onDateFilterChange: (value: string) => void }) => (
  <Select value={selectedDateFilter} onValueChange={onDateFilterChange}>
    <SelectTrigger aria-label={`Filter by date range, currently selected: ${selectedDateFilter === 'all' ? 'All Dates' : selectedDateFilter}`}>
      <SelectValue placeholder="Select date range" />
    </SelectTrigger>
    <SelectContent>
      {DATE_FILTER_OPTIONS.map((filter) => (
        <SelectItem key={filter.value} value={filter.value}>
          {filter.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
))

const MemoizedLocationFilter = memo(({ selectedLocation, onLocationChange }: { selectedLocation: string; onLocationChange: (value: string) => void }) => (
  <JobLocationsFromJobs
    value={selectedLocation}
    onValueChange={onLocationChange}
    ariaLabel={`Filter by location, currently selected: ${selectedLocation === 'all' ? 'All Locations' : selectedLocation}`}
  />
))

export const OptimizedJobFilters = memo(({
  selectedRole,
  onRoleChange,
  selectedLocation,
  onLocationChange,
  selectedProjectType,
  onProjectTypeChange,
  selectedDateFilter,
  onDateFilterChange,
}: OptimizedJobFiltersProps) => {
  return (
    <div className="bg-card p-6 rounded-lg border mb-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MemoizedRoleSelect selectedRole={selectedRole} onRoleChange={onRoleChange} />
        <MemoizedLocationFilter selectedLocation={selectedLocation} onLocationChange={onLocationChange} />
        <MemoizedProjectTypeSelect selectedProjectType={selectedProjectType} onProjectTypeChange={onProjectTypeChange} />
        <MemoizedDateFilterSelect selectedDateFilter={selectedDateFilter} onDateFilterChange={onDateFilterChange} />
      </div>
    </div>
  )
})

OptimizedJobFilters.displayName = 'OptimizedJobFilters'